/**
 * ScopeExpansionHandler
 * 
 * Wrapper component that manages the provisional optimizer flow
 * for job additions. Provides context and modal handling for
 * scope expansion detection and resolution.
 * 
 * CATALYST+ TIER ONLY
 */

import { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { useProvisionalOptimizer } from '@/hooks/useProvisionalOptimizer';
import { useAutonomousOptimizer } from '@/hooks/useFeatureFlags';
import { ResolutionOptionsModal } from './ResolutionOptionsModal';
import type { ResolutionOption, BoardIssue } from '@/hooks/useProvisionalOptimizer';

interface ScopeExpansionContextValue {
  checkAndHandleScopeExpansion: (
    ticketId: string,
    addedMinutes: number,
    onComplete: (applied: boolean) => void
  ) => Promise<void>;
  isChecking: boolean;
}

const ScopeExpansionContext = createContext<ScopeExpansionContextValue | null>(null);

export function useScopeExpansion() {
  const context = useContext(ScopeExpansionContext);
  if (!context) {
    throw new Error('useScopeExpansion must be used within ScopeExpansionHandler');
  }
  return context;
}

interface ScopeExpansionHandlerProps {
  children: ReactNode;
}

export function ScopeExpansionHandler({ children }: ScopeExpansionHandlerProps) {
  const { enabled: optimizerEnabled, provisionalAddOn } = useAutonomousOptimizer();
  
  const {
    isLoading,
    result,
    scopeExpanded,
    options,
    selectedOption,
    checkScopeExpansion,
    selectOption,
    applySelectedOption,
    reset,
  } = useProvisionalOptimizer();

  const [modalOpen, setModalOpen] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<((applied: boolean) => void) | null>(null);
  const [currentIssue, setCurrentIssue] = useState<BoardIssue | null>(null);

  const checkAndHandleScopeExpansion = useCallback(async (
    ticketId: string,
    addedMinutes: number,
    onComplete: (applied: boolean) => void
  ) => {
    // Skip if not Catalyst+ tier
    if (!optimizerEnabled || !provisionalAddOn) {
      onComplete(false);
      return;
    }
    
    // Only check if significant time was added
    if (addedMinutes <= 0) {
      onComplete(false);
      return;
    }

    const checkResult = await checkScopeExpansion(ticketId, addedMinutes);

    if (!checkResult) {
      // Error occurred, proceed without resolution
      onComplete(false);
      return;
    }

    // Check if scope expansion was detected
    const scopeIssue = checkResult.issuesDetected.find(
      issue => issue.type === 'SCOPE_EXPANDED'
    );

    if (scopeIssue && checkResult.resolutionOptions.length > 0) {
      // Show modal for user to choose resolution
      setCurrentIssue(scopeIssue);
      setPendingCallback(() => onComplete);
      setModalOpen(true);
    } else {
      // No scope expansion or no options available
      onComplete(false);
    }
  }, [checkScopeExpansion, optimizerEnabled, provisionalAddOn]);

  const handleModalClose = useCallback((open: boolean) => {
    if (!open) {
      reset();
      setCurrentIssue(null);
      if (pendingCallback) {
        pendingCallback(false);
        setPendingCallback(null);
      }
    }
    setModalOpen(open);
  }, [reset, pendingCallback]);

  const handleApply = useCallback(async () => {
    const success = await applySelectedOption();
    if (success) {
      reset();
      setCurrentIssue(null);
      if (pendingCallback) {
        pendingCallback(true);
        setPendingCallback(null);
      }
    }
    return success;
  }, [applySelectedOption, reset, pendingCallback]);

  const handleSkip = useCallback(() => {
    reset();
    setCurrentIssue(null);
    setModalOpen(false);
    if (pendingCallback) {
      pendingCallback(false);
      setPendingCallback(null);
    }
  }, [reset, pendingCallback]);

  return (
    <ScopeExpansionContext.Provider
      value={{
        checkAndHandleScopeExpansion,
        isChecking: isLoading,
      }}
    >
      {children}
      <ResolutionOptionsModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        issue={currentIssue}
        options={options}
        selectedOption={selectedOption}
        onSelectOption={selectOption}
        onApply={handleApply}
        onSkip={handleSkip}
        isLoading={isLoading}
        promiseIssues={result?.issuesDetected}
        issuePlans={result?.issuePlans}
      />
    </ScopeExpansionContext.Provider>
  );
}
